import { useEffect, useRef, useState, type FormEvent } from "react";
import type { ChannelPlatform, UpdateChannelInput } from "@qr-lifecycle/contracts";
import { ArrowLeft, LoaderCircle } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../api/client";
import { ErrorState, LoadingState, PageHeading } from "../components/States";

function toLocalDateTime(value: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function messageFor(error: unknown): string {
  return error instanceof ApiError ? error.message : "保存失败，请稍后重试。";
}

export function ChannelFormPage() {
  const { channelId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const platformRef = useRef<ChannelPlatform | null>(null);
  const [slug, setSlug] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [reminderDays, setReminderDays] = useState("1");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!channelId) return;
    setLoading(true);
    api.getChannel(channelId).then(
      ({ channel }) => {
        setName(channel.name);
        platformRef.current = channel.platform;
        setSlug(channel.slug);
        setExpiresAt(toLocalDateTime(channel.expiresAt));
        setReminderDays(String(channel.remindBeforeMinutes / 1_440));
        setLoading(false);
      },
      (error: unknown) => {
        setLoadError(messageFor(error));
        setLoading(false);
      },
    );
  }, [channelId]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setSaveError(null);
    if (!channelId || !platformRef.current) {
      setSaving(false);
      return;
    }
    const input: UpdateChannelInput = {
      name: name.trim(),
      platform: platformRef.current,
      slug: slug.trim().toLowerCase(),
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      remindBeforeMinutes: Math.round(Number(reminderDays) * 1_440),
    };
    try {
      const { channel } = await api.updateChannel(channelId, input);
      navigate(`/channels/${channel.id}`, { replace: true });
    } catch (error) {
      setSaveError(messageFor(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page"><LoadingState label="正在读取频道…" /></div>;
  if (loadError) return <div className="page"><ErrorState message={loadError} /></div>;

  return (
    <div className="page page--narrow">
      <Link className="back-link" to={channelId ? `/channels/${channelId}` : "/"}>
        <ArrowLeft size={16} /> 返回频道
      </Link>
      <PageHeading
        eyebrow="辅助纠错"
        title="编辑频道"
        description="修正手机识别的名称、公开地址和提醒时间。新频道需在手机检测结果中确认创建。"
      />

      <form className="editor-card" onSubmit={submit}>
        <section className="form-section">
          <div className="form-section__heading"><span>1</span><div><h2>频道信息</h2><p>名称只在管理端显示，公开地址可以长期分享。</p></div></div>
          <div className="form-grid">
            <label className="field field--full">
              <span>频道名称</span>
              <input required maxLength={120} value={name} onChange={(event) => setName(event.target.value)} placeholder="例如：产品交流群 3 群" />
            </label>
            <label className="field field--full">
              <span>公开地址</span>
              <div className="input-prefix">
                <span>/q/</span>
                <input
                  required
                  minLength={3}
                  maxLength={64}
                  pattern="[a-z0-9][a-z0-9-]{1,62}[a-z0-9]"
                  value={slug}
                  onChange={(event) => setSlug(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                  placeholder="product-community"
                />
              </div>
              <small>保存后仍可修改，但已分享的旧地址会失效。</small>
            </label>
          </div>
        </section>

        <section className="form-section">
          <div className="form-section__heading"><span>2</span><div><h2>到期提醒</h2><p>若不确定准确时间，也可以先按平台常见周期设置。</p></div></div>
          <div className="form-grid form-grid--two">
            <label className="field">
              <span>预计到期时间</span>
              <input type="datetime-local" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} />
              <small>不知道时可以留空，稍后再补。</small>
            </label>
            <label className="field">
              <span>提前多少天提醒</span>
              <input
                required
                type="number"
                min="0"
                max="30"
                step="0.5"
                value={reminderDays}
                onChange={(event) => setReminderDays(event.target.value)}
              />
              <small>默认提前 1 天发送手机通知。</small>
            </label>
          </div>
        </section>

        {saveError ? <p className="form-error" role="alert">{saveError}</p> : null}
        <footer className="editor-card__footer">
          <Link className="button button--ghost" to={channelId ? `/channels/${channelId}` : "/"}>取消</Link>
          <button className="button button--primary" disabled={saving}>
            {saving ? <LoaderCircle className="spin" size={17} /> : null}
            保存修改
          </button>
        </footer>
      </form>
    </div>
  );
}
